import {Button, Chip, Dialog, DialogBody, DialogFooter, DialogHeader, Typography} from "@material-tailwind/react";
import React, {useState} from "react";
import {BsPrinter} from "react-icons/bs";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import Dict from "../../helper/dict";
import PersianDateTime from "../system/persianDateTime";
import gregorianToJalali from "../../helper/jalaaliDateTime";

const DATE_PATTERN = /^\d{4}-\d{1,2}-\d{1,2}$/;

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const formatPrintValue = (value) => value ? persianNumberFormat(value, true) : "";

const formatPrintPlainNumber = (value) => value ? persianNumberFormat(value) : "";

const formatLatinDigits = (value) => String(value ?? "")
  .replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
  .replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));

const formatPrintDate = (value) => {
  if (!value) return "";

  const datePart = String(value).trim().split(/[ T]+/)[0];
  if (!DATE_PATTERN.test(datePart)) {
    return persianNumberFormat(datePart);
  }

  const [day, month, year] = String(gregorianToJalali(datePart)).split("-");
  const jalaliDate = year
    ? [year, month.padStart(2, "0"), day.padStart(2, "0")].join("-")
    : gregorianToJalali(datePart);

  return persianNumberFormat(jalaliDate);
};

const getSourceAmount = (source) => Number(source?.amount || source?.available_amount || 0);

const getUserPaidAmount = (source) => Number(source?.user_paid_amount || 0);

const getTotalPaidAmount = (source) => Number(source?.total_paid || 0);

export function WithdrawalSourcesModal({withdrawal, open, setOpen, showPrintButton = true}) {
  const handleOpen = () => setOpen(!open);
  const [hoveredGroupKey, setHoveredGroupKey] = useState(null);
  const sources = Array.isArray(withdrawal?.sources) ? withdrawal.sources : [];
  const totalSourcesAmount = sources.reduce((sum, source) => sum + getSourceAmount(source), 0);
  const groupedSources = Object.values(sources.reduce((groups, source) => {
    const productId = source?.service_id || source?.reservation_id || source?.product_id || "unknown";
    const productType = source?.product_type || "unknown";
    const key = `${productType}-${productId}`;

    if (!groups[key]) {
      groups[key] = {
        key,
        productId,
        productType,
        branchTitle: source?.branch_title,
        fieldTitle: source?.field_title,
        rows: [],
        total: 0,
      };
    }

    groups[key].rows.push(source);
    groups[key].total += Number(source?.amount || source?.available_amount || 0);
    return groups;
  }, {}));
  const status = withdrawal?.status;
  const statusLabel = status === "accepted" ? "انجام شده" : status === "new" ? "در انتظار بررسی" : status === "rejected" ? "رد شده" : status === "canceled" ? "لغو شده" : status;
  const statusColor = status === "accepted" ? "green" : status === "new" ? "amber" : "red";

  const renderProductLabel = (productType) => {
    if (productType === "service") return "خدمت";
    if (productType === "reservation" || productType === "consultation") return "رزرو";
    return productType && productType !== "unknown" ? Dict(productType) : "بدون منبع";
  };

  const renderEarningType = (source) => {
    if (source.product_type === "service" && source.source_type === "agent_share") {
      return "درآمد خدمت";
    }
    if (source.product_type && source.source_type) {
      return `${Dict(source.product_type)} - ${Dict(source.source_type)}`;
    }
    return source.source_type ? Dict(source.source_type) : "-";
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=1024,height=768");
    if (!printWindow) {
      return;
    }

    const serviceSources = sources.filter(source => source.product_type === "service");
    const consultationSources = sources.filter(source =>
      source.product_type === "reservation" || source.product_type === "consultation"
    );
    const uniqueServiceSources = Array.from(serviceSources.reduce((uniqueSources, source, index) => {
      const serviceId = source?.service_id ?? source?.product_id;
      const key = serviceId == null ? `source-${index}` : String(serviceId);

      if (!uniqueSources.has(key)) {
        uniqueSources.set(key, source);
      }

      return uniqueSources;
    }, new Map()).values());
    const hasServiceSource = serviceSources.length > 0;
    const consultationCount = consultationSources.length;
    const serviceCount = uniqueServiceSources.length;
    const serviceSecondStepCount = serviceSources.filter(source => Number(source?.settlement_step_number) === 2).length;
    const serviceFourthStepCount = serviceSources.filter(source => Number(source?.settlement_step_number) === 4).length;
    const serviceTotal = uniqueServiceSources.reduce((sum, source) => sum + getTotalPaidAmount(source), 0);
    const consultationTotal = consultationSources.reduce((sum, source) => sum + getUserPaidAmount(source), 0);
    const firstSource = sources[0] || {};
    const documentNumber = withdrawal?.id || "";
    const requestDate = withdrawal?.created_at || firstSource?.created_at || "";
    const advisorName = withdrawal?.person_name || withdrawal?.agent_name || "";
    const advisorNationalCode = withdrawal?.national_code || withdrawal?.person_national_code || withdrawal?.agent_national_code || "";
    const advisorId = withdrawal?.person_id || withdrawal?.agent_id || "";
    const agentRequestId = withdrawal?.agent_request_id || "";
    const bankSheba = withdrawal?.bank_sheba || "";
    const consultationDescription = `${persianNumberFormat(consultationCount, true)} مشاوره`;
    const serviceDescription = `${persianNumberFormat(serviceCount, true)} خدمت`;
    const printSources = sources.length < 10
      ? [...sources, ...Array.from({length: 10 - sources.length}, () => null)]
      : sources;
    const sourceRows = printSources.map((source, index) => {
      const sourceId = source?.service_id || source?.reservation_id || source?.product_id || "";
      const serviceTitle = [
        source ? renderProductLabel(source?.product_type) : "",
        sourceId ? `#${formatPrintValue(sourceId)}` : "",
        source?.field_title || "",
        source?.branch_title || "",
      ].filter(Boolean).join(" - ");
      const customerName = source?.user_name || source?.person_name || source?.customer_name || "";
      const customerPhone = source?.user_phone || source?.phone || source?.person_phone || "";
      const reservationDate = source?.reservation_date || "";
      const amount = source ? getSourceAmount(source) : "";
      const userPaidAmount = source?.user_paid_amount || "";
      const sourceType = source ? renderEarningType(source) : "";

      return `
        <tr>
          <td rowspan="3" class="serial">${formatPrintPlainNumber(index + 1)}</td>
          <td colspan="3">نام مشتری: <span>${escapeHtml(customerName)}</span></td>
          <td colspan="3">شماره تماس: <span dir="ltr">${escapeHtml(formatLatinDigits(customerPhone))}</span></td>
        </tr>
        <tr>
          <td colspan="3">نوع سرویس: <span>${escapeHtml(sourceType)}</span></td>
          <td colspan="3">تاریخ رزرو: <span class="print-date" dir="ltr">${escapeHtml(formatPrintDate(reservationDate))}</span></td>
        </tr>
        <tr>
          <td colspan="3">مبلغ کل: <span>${userPaidAmount ? formatPrintValue(userPaidAmount) + " ریال" : ""}</span></td>
          <td colspan="3">مبلغ قابل پرداخت: <span>${amount ? formatPrintValue(amount) + " ریال" : ""}</span>${serviceTitle ? `<small>${escapeHtml(serviceTitle)}</small>` : ""}</td>
        </tr>
      `;
    }).join("");

    const html = `
      <!doctype html>
      <html lang="fa" dir="rtl">
        <head>
          <meta charset="utf-8" />
          <title>سند پرداخت ${escapeHtml(documentNumber)}</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              background: #f2f2f2;
              color: #000;
              font-family: Tahoma, Arial, sans-serif;
              direction: rtl;
            }
            .page {
              width: 210mm;
              min-height: 297mm;
              margin: 0 auto;
              padding: 10mm;
              background: #fff;
            }
            .payment-sheet {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              direction: rtl;
            }
            .payment-sheet td {
              height: 20px;
              border: 1px solid #000;
              padding: 3px 6px;
              text-align: right;
              vertical-align: middle;
              font-size: 10px;
              line-height: 1.45;
              overflow-wrap: anywhere;
            }
            .payment-sheet span {
              font-weight: 700;
            }
            .print-date {
              direction: ltr;
              unicode-bidi: isolate;
              display: inline-block;
            }
            .center {
              text-align: center;
            }
            .title {
              height: 34px;
              text-align: center !important;
              font-size: 13px !important;
              font-weight: 700;
            }
            .doc-meta {
              height: 28px;
              font-weight: 700;
            }
            .tall {
              height: 38px;
            }
            .desc {
              height: 38px;
              white-space: pre-wrap;
            }
            .signature {
              height: 220px;
              text-align: center !important;
              font-weight: 700;
              vertical-align: top !important;
              padding: 30px 36px 12px !important;
              line-height: 2.4 !important;
            }
            .signature-label {
              display: block;
              min-height: 44px;
              padding: 0 24px;
              white-space: nowrap;
              word-spacing: 8px;
            }
            .list-title {
              height: 24px;
              text-align: center !important;
              font-weight: 700;
              background: #f7f7f7;
            }
            .serial {
              width: 30px;
              text-align: center !important;
              font-size: 11px !important;
              font-weight: 700;
            }
            .box {
              display: inline-block;
              min-width: 14px;
              width: auto;
              height: 12px;
              padding: 0 2px;
              margin: 0 8px 0 24px;
              border: 1px solid #000;
              text-align: center;
              font-size: 10px;
              font-weight: 700;
              line-height: 11px;
              vertical-align: -2px;
            }
            .payment-sheet small {
              display: block;
              margin-top: 2px;
              color: #333;
              font-size: 9px;
            }
            .spacer td {
              height: 12px;
              border-right-color: transparent;
              border-left-color: transparent;
            }
            .print-actions {
              position: sticky;
              top: 0;
              display: flex;
              justify-content: flex-end;
              gap: 8px;
              padding: 10px;
              background: #fff;
              border-bottom: 1px solid #e5e7eb;
            }
            .print-actions button {
              cursor: pointer;
              border: 0;
              border-radius: 6px;
              background: #111827;
              color: white;
              padding: 8px 14px;
              font-family: inherit;
              font-size: 12px;
              font-weight: 700;
            }
            @media print {
              body { background: #fff; }
              .page {
                width: auto;
                min-height: auto;
                margin: 0;
                padding: 0;
              }
              .print-actions { display: none; }
              @page { size: A4 portrait; margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="print-actions">
            <button type="button" onclick="window.print()">چاپ</button>
          </div>
          <main class="page">
            <table class="payment-sheet">
              <colgroup>
                <col style="width: 2%" />
                <col style="width: 16.3%" />
                <col style="width: 16.3%" />
                <col style="width: 16.4%" />
                <col style="width: 16.3%" />
                <col style="width: 16.3%" />
                <col style="width: 16.4%" />
              </colgroup>
              <tbody>
                <tr>
                  <td colspan="4" rowspan="2" class="doc-meta">شماره سند ${escapeHtml(formatPrintPlainNumber(documentNumber))}</td>
                  <td colspan="3" rowspan="2" class="doc-meta">تاریخ درخواست: <span class="print-date" dir="ltr">${escapeHtml(formatPrintDate(requestDate))}</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2" class="title">سند پرداخت وجه در پلتفرم مباشر</td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="4" rowspan="2">نام مشاور/ شرکت: <span>${escapeHtml(advisorName)}</span></td>
                  <td colspan="3" rowspan="2">شناسه مشاور: <span>${escapeHtml(formatPrintPlainNumber(advisorId))}</span>${agentRequestId ? ` / شناسه درخواست: <span>${escapeHtml(formatPrintPlainNumber(agentRequestId))}</span>` : ""}</td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2">کد ملی/ شناسه ملی: <span dir="ltr">${escapeHtml(formatLatinDigits(advisorNationalCode))}</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2" class="tall">
                    نوع سرویس:
                    مشاوره <span class="box">${consultationCount > 0 ? "✓" : ""}</span>
                    خدمات <span class="box">${hasServiceSource ? "✓" : ""}</span>
                  </td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2" class="desc">شرح مشاوره انجام شده: <span>${escapeHtml(consultationDescription)}</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2" class="desc">شرح خدمت انجام شده: <span>${escapeHtml(serviceDescription)}</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="4" rowspan="2">مبلغ کل مشاوره: <span>${formatPrintValue(consultationTotal)} ریال</span></td>
                  <td colspan="3" rowspan="2">مبلغ کل خدمات: <span>${formatPrintValue(serviceTotal)} ریال</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2" class="tall">
                    نوع پرداخت:
                    مرحله ای <span class="box">${formatPrintPlainNumber(serviceSecondStepCount)}</span>
                    تکمیلی <span class="box">${formatPrintPlainNumber(serviceFourthStepCount)}</span>
                  </td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="4" rowspan="2">درصد مشاور: جهت مشاوره 70 خدمت 60</td>
                  <td colspan="3" rowspan="2">مبلغ قابل پرداخت: <span>${formatPrintValue(withdrawal?.value || totalSourcesAmount)} ریال</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="7" rowspan="2">شماره شبا: <span dir="ltr">${escapeHtml(formatLatinDigits(bankSheba))}</span></td>
                </tr>
                <tr></tr>
                <tr>
                  <td colspan="4" rowspan="12" class="signature"><span class="signature-label">پشتیبان مشاوران</span></td>
                  <td colspan="3" rowspan="12" class="signature"><span class="signature-label">سرپرست مباشر</span></td>
                </tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr>
                  <td colspan="4" rowspan="12" class="signature"><span class="signature-label">مدیریت مباشر</span></td>
                  <td colspan="3" rowspan="12" class="signature"><span class="signature-label">مسئول مالی</span></td>
                </tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr></tr>
                <tr class="spacer"><td colspan="7"></td></tr>
                <tr>
                  <td colspan="7" class="list-title">لیست واریز‌های مربوط به سند پرداخت</td>
                </tr>
                ${sourceRows}
              </tbody>
            </table>
          </main>
          <script>
            window.addEventListener("load", function () {
              window.focus();
              setTimeout(function () {
                window.print();
              }, 250);
            });
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} handler={handleOpen} size="xl" animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}>
      <DialogHeader className="border-b border-blue-gray-50">
        <div className="flex w-full flex-col gap-2 textRight">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Typography variant="h6" color="blue-gray">منابع مبلغ برداشت</Typography>
            {status && <Chip variant="ghost" value={statusLabel} color={statusColor} className="w-fit"/>}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <Typography color="gray" variant="small">
              {"مبلغ برداشت: " + persianNumberFormat(withdrawal?.value || 0, true) + " ریال"}
            </Typography>
            <Typography color="gray" variant="small">
              {"تعداد منبع: " + persianNumberFormat(sources.length, true)}
            </Typography>
            <Typography color="gray" variant="small">
              {"جمع منابع: " + persianNumberFormat(totalSourcesAmount, true) + " ریال"}
            </Typography>
          </div>
        </div>
      </DialogHeader>
      <DialogBody className="max-h-[70vh] overflow-y-auto p-0">
        {sources.length === 0 ? (
          <Typography color="blue-gray" className="py-10 text-center">
            منبعی برای این برداشت ثبت نشده است
          </Typography>
        ) : (
          <div className="overflow-x-auto p-4">
            <table className="w-full min-w-[760px] border-separate border-spacing-0 text-left">
              <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-blue-gray-100">
                <th className="w-10 px-3 py-2 text-center">
                  <Typography className="text-xs font-semibold text-blue-gray-500">#</Typography>
                </th>
                <th className="px-3 py-2 text-center">
                  <Typography className="text-xs font-semibold text-blue-gray-500">مبلغ</Typography>
                </th>
                <th className="px-3 py-2 text-center">
                  <Typography className="text-xs font-semibold text-blue-gray-500">نوع درآمد</Typography>
                </th>
                <th className="px-3 py-2 text-center">
                  <Typography className="text-xs font-semibold text-blue-gray-500">تاریخ رزرو</Typography>
                </th>
                <th className="px-3 py-2 text-center">
                  <Typography className="text-xs font-semibold text-blue-gray-500">تاریخ تسویه</Typography>
                </th>
                <th className="px-3 py-2 text-center">
                  <Typography className="text-xs font-semibold text-blue-gray-500">شناسه تراکنش</Typography>
                </th>
              </tr>
              </thead>
              <tbody>
              {groupedSources.map((group, groupIndex) => {
                const isGrouped = group.rows.length > 1;
                const isGroupHovered = hoveredGroupKey === group.key;
                const firstSource = group.rows[0];
                const groupSourceId = firstSource?.service_id || firstSource?.reservation_id || firstSource?.product_id;
                const groupHoverBg = isGroupHovered ? "bg-blue-gray-50" : "bg-white";
                const rowBg = isGroupHovered ? "bg-blue-gray-50" : "bg-white";

                return (
                  <React.Fragment key={group.key}>
                    {groupIndex > 0 && (
                      <tr>
                        <td colSpan={6} className="h-2 border-0 bg-transparent p-0"/>
                      </tr>
                    )}
                    {isGrouped && (
                      <tr
                        className={`${groupHoverBg} transition-colors`}
                        onMouseEnter={() => setHoveredGroupKey(group.key)}
                        onMouseLeave={() => setHoveredGroupKey(null)}
                      >
                        <td colSpan={6} className="rounded-t border-x border-t border-blue-gray-300 border-r-2 px-3 py-2">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <Typography color="blue-gray" variant="small" className="font-semibold">
                              {renderProductLabel(group.productType)}
                            </Typography>
                            {groupSourceId && (
                              <Typography color="blue-gray" variant="small" className="font-semibold tabular-nums">
                                #{persianNumberFormat(groupSourceId, true)}
                              </Typography>
                            )}
                            {firstSource?.field_title && (
                              <Typography color="gray" variant="small">{firstSource.field_title}</Typography>
                            )}
                            {firstSource?.branch_title && (
                              <Typography color="gray" variant="small">{firstSource.branch_title}</Typography>
                            )}
                            <div className="flex-1"/>
                            <Typography color="gray" variant="small">
                              {persianNumberFormat(group.rows.length, true) + " پرداخت"}
                            </Typography>
                            <Typography color="blue-gray" variant="small" className="font-semibold tabular-nums">
                              {persianNumberFormat(group.total, true) + " ریال"}
                            </Typography>
                          </div>
                        </td>
                      </tr>
                    )}
                    {group.rows.map((source, index) => {
                      const {
                        amount, wallet_transaction_id, product_id, service_id, reservation_id,
                        branch_title, field_title, wallet_product_log_id, settled_at, created_at, reservation_date
                      } = source;
                      const sourceAmount = amount || source.available_amount;
                      const sourceDate = settled_at || created_at;
                      const sourceIndex = sources.findIndex(item => item === source);
                      const sourceId = service_id || reservation_id || product_id;
                      const isLastInGroup = index === group.rows.length - 1;
                      const isSingle = !isGrouped;
                      const frameTop = isSingle ? "border-t border-blue-gray-300" : "";
                      const frameBottom = isLastInGroup ? "border-b border-blue-gray-300" : "border-b border-blue-gray-100";
                      const firstCellFrame = `${frameTop} ${frameBottom} border-r-2 border-r-blue-gray-300 ${isSingle ? "rounded-r" : isLastInGroup ? "rounded-br" : ""}`;
                      const middleCellFrame = `${frameTop} ${frameBottom}`;
                      const lastCellFrame = `${frameTop} ${frameBottom} border-l border-l-blue-gray-300 ${isSingle ? "rounded-l" : isLastInGroup ? "rounded-bl" : ""}`;

                      return (
                        <tr
                          key={`${wallet_transaction_id || "source"}-${group.key}-${index}`}
                          className={`transition-colors duration-150 ${rowBg}`}
                          onMouseEnter={() => setHoveredGroupKey(group.key)}
                          onMouseLeave={() => setHoveredGroupKey(null)}
                        >
                          <td className={`${firstCellFrame} px-3 py-2`}>
                            <Typography color="gray" variant="small" className="text-center tabular-nums">
                              {persianNumberFormat(sourceIndex + 1, true)}
                            </Typography>
                          </td>
                          <td className={`${middleCellFrame} px-3 py-2`}>
                            <Typography color="blue-gray" className="text-center font-semibold tabular-nums">
                              {persianNumberFormat(sourceAmount, true)}
                              <span className="mr-1 text-xs font-normal text-blue-gray-400">ریال</span>
                            </Typography>
                          </td>
                          <td className={`${middleCellFrame} px-3 py-2`}>
                            <Typography color="blue-gray" variant="small" className="text-center">
                              {renderEarningType(source)}
                            </Typography>
                            {!isGrouped && (sourceId || field_title) && (
                              <Typography color="gray" variant="small" className="mt-0.5 text-center tabular-nums opacity-60">
                                {[
                                  sourceId ? `${renderProductLabel(group.productType)} #${persianNumberFormat(sourceId, true)}` : null,
                                  field_title,
                                  branch_title,
                                ].filter(Boolean).join(" · ")}
                              </Typography>
                            )}
                          </td>
                          <td className={`${middleCellFrame} px-3 py-2`}>
                            <Typography color="blue-gray" variant="small" dir="ltr" className="text-center tabular-nums">
                              <PersianDateTime value={reservation_date}/>
                            </Typography>
                          </td>
                          <td className={`${middleCellFrame} px-3 py-2`}>
                            <Typography color="blue-gray" variant="small" dir="ltr" className="text-center tabular-nums">
                              <PersianDateTime value={sourceDate}/>
                            </Typography>
                          </td>
                          <td className={`${lastCellFrame} px-3 py-2`}>
                            <Typography color="blue-gray" variant="small" className="text-center font-semibold tabular-nums">
                              {wallet_transaction_id ? persianNumberFormat(wallet_transaction_id, true) : "-"}
                            </Typography>
                            {wallet_product_log_id && (
                              <Typography color="gray" variant="small" className="text-center opacity-60">
                                {"log " + persianNumberFormat(wallet_product_log_id, true)}
                              </Typography>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              </tbody>
            </table>
          </div>
        )}
      </DialogBody>
      <DialogFooter className="border-t border-blue-gray-50">
        {showPrintButton && (
          <Button variant="outlined" color="gray" onClick={handlePrint} disabled={sources.length === 0} className="ml-2 flex items-center gap-2">
            <BsPrinter className="h-4 w-4"/>
            <span>چاپ سند پرداخت</span>
          </Button>
        )}
        <Button variant="text" color="gray" onClick={handleOpen}>
          <span>بستن</span>
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
